import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, Ticket, ChevronRight, X, CheckCircle, AlertCircle, MessageSquare, CreditCard, Clock, Zap, Users, UserCheck, Calendar, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useLocation, Link } from 'react-router-dom';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const Header = ({ toggleSidebar }) => {
  const { user } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationRef = useRef(null);
  
  // Subscription dropdown state
  const [showSubscriptionDropdown, setShowSubscriptionDropdown] = useState(false);
  const subscriptionRef = useRef(null);
  
  // Generate breadcrumbs from path
  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(Boolean);
    return paths.length > 0 ? paths : ['dashboard'];
  };

  const breadcrumbs = getBreadcrumbs();

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/tickets/notifications/unread');
      setNotifications(response.data);
      setUnreadCount(response.data.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/tickets/notifications/${notificationId}/read`);
      // Immediately remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Mark all notifications as read
      await Promise.all(notifications.map(n => api.put(`/tickets/notifications/${n.id}/read`)));
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const dismissNotification = (notificationId) => {
    // Remove from UI immediately and mark as read in background
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setUnreadCount(prev => Math.max(0, prev - 1));
    // Mark as read in background (don't wait)
    api.put(`/tickets/notifications/${notificationId}/read`).catch(console.error);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ticket_response':
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'ticket_update':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'ticket_resolved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  // Fetch notifications
  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (subscriptionRef.current && !subscriptionRef.current.contains(event.target)) {
        setShowSubscriptionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
      <div className="flex items-center justify-between px-6 py-3">
        {/* Left Section - Logo and Name (Group or College) */}
        <div className="flex items-center gap-4">
          <button 
            onClick={toggleSidebar}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu size={24} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            {/* Show Group logo for GroupAdmin, Tenant logo for Admin, Cloud Campus for SuperAdmin */}
            {user?.role === 'SuperAdmin' ? (
              <>
                <div className="text-gray-800 font-semibold text-lg">Cloud Campus</div>
              </>
            ) : user?.role === 'GroupAdmin' ? (
              <>
                {user?.group_logo && (
                  <img 
                    src={user.group_logo} 
                    alt="Group Logo" 
                    className="h-10 w-10 object-contain rounded"
                  />
                )}
                <div className="text-gray-800 font-semibold text-lg">{user?.group_name || 'Group of Colleges'}</div>
              </>
            ) : (
              <>
                {user?.tenant_logo && (
                  <img 
                    src={user.tenant_logo} 
                    alt="College Logo" 
                    className="h-10 w-10 object-contain rounded"
                  />
                )}
                <div className="text-gray-800 font-semibold text-lg">{user?.tenant_name || 'College Name'}</div>
              </>
            )}
          </div>
        </div>

        {/* Right Section - Actions and Profile */}
        <div className="flex items-center gap-3">
          {/* Subscription Status Badge - For Admin and GroupAdmin */}
          {(user?.role === 'Admin' || user?.role === 'GroupAdmin') && subscriptionStatus && (
            <div className="relative" ref={subscriptionRef}>
              <button
                onClick={() => setShowSubscriptionDropdown(!showSubscriptionDropdown)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  subscriptionStatus.status === 'active' 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : subscriptionStatus.status === 'trial'
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : subscriptionStatus.status === 'grace_period'
                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
                data-testid="subscription-badge"
              >
                <span className={`w-2 h-2 rounded-full ${
                  subscriptionStatus.status === 'active' ? 'bg-green-500' :
                  subscriptionStatus.status === 'trial' ? 'bg-blue-500' :
                  subscriptionStatus.status === 'grace_period' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></span>
                <span className="hidden sm:inline">
                  {subscriptionStatus.status === 'active' ? 'Active' : 
                   subscriptionStatus.status === 'trial' ? 'Trial' :
                   subscriptionStatus.status === 'grace_period' ? 'Grace Period' : 'Inactive'}
                </span>
                {subscriptionStatus.days_remaining !== null && (
                  <span className="hidden md:inline text-xs opacity-75">
                    | {subscriptionStatus.days_remaining}d
                  </span>
                )}
                <ChevronDown size={14} />
              </button>

              {/* Subscription Dropdown */}
              {showSubscriptionDropdown && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
                  {/* Header */}
                  <div className={`px-4 py-3 ${
                    subscriptionStatus.status === 'active' ? 'bg-green-50' :
                    subscriptionStatus.status === 'trial' ? 'bg-blue-50' :
                    subscriptionStatus.status === 'grace_period' ? 'bg-yellow-50' : 'bg-red-50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-gray-600" />
                        <span className="font-semibold text-gray-900">Subscription</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        subscriptionStatus.status === 'active' ? 'bg-green-200 text-green-800' :
                        subscriptionStatus.status === 'trial' ? 'bg-blue-200 text-blue-800' :
                        subscriptionStatus.status === 'grace_period' ? 'bg-yellow-200 text-yellow-800' : 
                        'bg-red-200 text-red-800'
                      }`}>
                        {subscriptionStatus.status === 'active' ? 'Active' : 
                         subscriptionStatus.status === 'trial' ? 'Trial' :
                         subscriptionStatus.status === 'grace_period' ? 'Grace Period' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-3">
                    {/* Plan & Days */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm text-gray-600">Plan</span>
                      </div>
                      <span className="font-semibold text-gray-900 capitalize">
                        {subscriptionStatus.plan_name || subscriptionStatus.plan_code || subscriptionStatus.plan || 'Free'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-500" />
                        <span className="text-sm text-gray-600">Days Left</span>
                      </div>
                      <span className={`font-semibold ${
                        subscriptionStatus.days_remaining > 30 ? 'text-green-600' :
                        subscriptionStatus.days_remaining > 7 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {subscriptionStatus.days_remaining !== null && subscriptionStatus.days_remaining !== undefined 
                          ? subscriptionStatus.days_remaining 
                          : '∞'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-purple-500" />
                        <span className="text-sm text-gray-600">Valid Till</span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {subscriptionStatus.subscription_end_date || subscriptionStatus.end_date
                          ? formatDate(subscriptionStatus.subscription_end_date || subscriptionStatus.end_date)
                          : 'N/A'}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className="border-t pt-3">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-indigo-500" />
                          <span className="text-gray-600">Students (This Year)</span>
                        </div>
                        <span className="font-medium">
                          {subscriptionStatus.yearly_students || 0} / {subscriptionStatus.max_yearly_admissions > 0 ? subscriptionStatus.max_yearly_admissions.toLocaleString('en-IN') : '∞'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <div className="flex items-center gap-2">
                          <UserCheck className="h-4 w-4 text-teal-500" />
                          <span className="text-gray-600">Staff (This Year)</span>
                        </div>
                        <span className="font-medium">
                          {subscriptionStatus.yearly_staff || 0} / {subscriptionStatus.max_yearly_staff > 0 ? subscriptionStatus.max_yearly_staff.toLocaleString('en-IN') : '∞'}
                        </span>
                      </div>
                    </div>

                    {/* Payment Status */}
                    <div className="border-t pt-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Payment</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          subscriptionStatus.payment_status === 'paid' || subscriptionStatus.status === 'active'
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {subscriptionStatus.payment_status === 'paid' || subscriptionStatus.status === 'active' ? '✓ Paid' : '⏳ Pending'}
                        </span>
                      </div>
                    </div>

                    {/* Warning if expiring */}
                    {subscriptionStatus.days_remaining !== null && subscriptionStatus.days_remaining <= 7 && (
                      <div className={`p-2 rounded-lg text-xs ${
                        subscriptionStatus.is_grace_period ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      }`}>
                        ⚠️ {subscriptionStatus.is_grace_period 
                          ? `Grace period: ${subscriptionStatus.days_remaining} days left` 
                          : 'Subscription expiring soon!'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Generate Ticket */}
          <Link 
            to="/tickets/create"
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Ticket size={18} />
            <span className="hidden md:inline">Generate Ticket</span>
          </Link>

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors" 
              title="Notifications"
            >
              <Bell size={20} className="text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full hover:bg-blue-200 transition-colors"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p>No new notifications</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div 
                        key={notification.id}
                        className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 flex gap-3"
                      >
                        <div className="flex-shrink-0 mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{notification.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(notification.created_at)}
                          </p>
                        </div>
                        <button 
                          onClick={() => dismissNotification(notification.id)}
                          className="flex-shrink-0 p-1 hover:bg-gray-200 rounded"
                          title="Dismiss"
                        >
                          <X className="h-4 w-4 text-gray-400" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                    <Link 
                      to="/tickets/report"
                      className="text-sm text-blue-600 hover:text-blue-800"
                      onClick={() => setShowNotifications(false)}
                    >
                      View all tickets →
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Profile */}
          <Link to="/profile" className="flex items-center gap-2 hover:bg-gray-50 p-1.5 rounded-lg transition-colors">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
              {user?.full_name?.split(' ').map(n => n[0]).join('').substring(0, 2) || 'U'}
            </div>
          </Link>
        </div>
      </div>
      
      {/* Breadcrumb Navigation */}
      <div className="px-6 py-2 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-2 text-sm">
          <Link to="/dashboard" className="text-gray-600 hover:text-blue-600 transition-colors">
            Home
          </Link>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <ChevronRight size={14} className="text-gray-400" />
              <span className={index === breadcrumbs.length - 1 ? "text-blue-600 font-medium capitalize" : "text-gray-600 capitalize"}>
                {crumb.replace(/-/g, ' ')}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;
